import { CURRENT_LANG, ES } from "@/constants/core";
import { identifyUser, trackLogin } from "@/core/amplitude";
import {
  auth,
  firebaseConfirmSigninLink,
  firebaseLogin,
  firebaseLogout,
  firebaseSigninEmailLink,
  getCurrentUser,
} from "@/core/firebase";
import {
  authenticateUser,
  AxiosError,
  createSupplierWithoutId,
  getSupplierRoleById,
  getTenantProfile,
  getUserProfile,
  TSupplier,
  TSupplierCommercial,
  TSupplierRole,
} from "@/http";
import { TLoginForm, TSignupForm } from "@/models/auth";
import { Supplier } from "@/models/supplier";
import { signInWithCustomToken, User } from "firebase/auth";
import { create } from "zustand";

import { refreshSeeriApiConfig } from "@/config/seeri-api";
import { showToastError } from "@/hooks/useToast";
import { requestFcmToken } from "@/core/firebase";
import { upsertFcmToken } from "@/http";
import { logError } from "@/utils/errors";

type TAuthStatus = {
  user: Supplier;
  isAuth: boolean;
  isUSA: boolean;
  isCO: boolean;
  setUser: (user: Supplier) => void;
  isCommercial: boolean;
  isLoadingAuth: boolean;
  setCommercial: (user: Supplier | null, commercial: TSupplierCommercial | null) => void;
  commercial: TSupplierCommercial | null;
  role: TSupplierRole | null;
  authMethod: "password" | "otp" | null;
  twoFACompleted: boolean;
  setAuthMethod: (method: "password" | "otp" | null) => void;
  setTwoFACompleted: (completed: boolean) => void;
  login: (values: TLoginForm) => Promise<void>;
  loginAndGetJwtToken: (
    email: string,
    password: string
  ) => Promise<{ jwtSupplierId: string; jwtRoles: string[] }>;
  loginWithCustomTokenAndGetUser: (token: string) => Promise<User>;
  loginWithSupplier: (email: string, password: string, supplierId: string) => Promise<void>;
  emailLogin: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  loginOtp: (token: string) => Promise<void>;
  changePassword: (password: string) => Promise<void>;
  signup: (values: TSignupForm) => Promise<void>;
  setSupplierToUser: (email: string, supplierId: string, role?: string) => Promise<void>;
  setSupplierOrLogout: (user: User) => Promise<void>;
};

export const useAuth = create<TAuthStatus>()((set, get) => ({
  isUSA: get()?.user?.countryCode === "us",
  isCO: get()?.user?.countryCode === "co",
  isCommercial: false,
  isLoadingAuth: false,
  setCommercial: async (user, commercial) => {
    set({ isLoadingAuth: true });
    let role: TSupplierRole | null = null;

    if (commercial?.roleId && user?.id) {
      role = await getSupplierRoleById(user.id, commercial.roleId);
    }

    set(() => ({ commercial, isCommercial: !!commercial, role, isLoadingAuth: false }));
  },
  commercial: null,
  role: null,
  user: {} as TSupplier,
  isAuth: false,
  authMethod: null,
  twoFACompleted: false,
  setUser: (user) =>
    set(() => ({
      user,
      isAuth: !!user.id,
      isUSA: user?.countryCode === "us",
      isCO: user?.countryCode === "co",
    })),
  setAuthMethod: (method) => set(() => ({ authMethod: method })),
  setTwoFACompleted: (completed) => set(() => ({ twoFACompleted: completed })),
  logout: async () => {
    await firebaseLogout();
    get().setUser({} as TSupplier);
    get().setCommercial(null, null);
    localStorage.clear();
  },
  login: async ({ email, password }) => {
    const credentials = await firebaseLogin(email, password);
    await setSupplierOrLogout(credentials.user);
  },
  loginAndGetJwtToken: async (email: string, password: string) => {
    const credentials = await firebaseLogin(email, password);
    const userToken = await credentials.user.getIdTokenResult();
    const jwtSupplierId = userToken?.claims["supplier_id"];
    const jwtRoles = userToken?.claims["roles"];
    return { jwtSupplierId, jwtRoles };
  },
  loginWithCustomTokenAndGetUser: async (token: string) => {
    const credentials = await signInWithCustomToken(auth, token);
    return credentials.user;
  },
  setSupplierToUser: async (email: string, supplierId: string, role?: string) => {
    await authenticateUser({ email, supplierId, role });
  },
  loginWithSupplier: async (email: string, password: string, supplierId: string) => {
    try {
      const authResponse = await authenticateUser({
        role: "SUPPLIER",
        supplierId,
        email,
      });

      if (authResponse.customToken) {
        const userCredential = await signInWithCustomToken(auth, authResponse.customToken);
        await setSupplierOrLogout(userCredential.user);
      } else {
        const credentials = await firebaseLogin(email, password);
        await setSupplierOrLogout(credentials.user);
      }
    } catch (error) {
      const credentials = await firebaseLogin(email, password);
      await setSupplierOrLogout(credentials.user);
    }
  },
  emailLogin: async () => {
    const validSigninLink = await firebaseConfirmSigninLink();
    if (validSigninLink) {
      const email = window.localStorage.getItem("emailForSignIn") || "";
      window.localStorage.removeItem("emailForSignIn");
      const credentials = await firebaseSigninEmailLink(email);
      await setSupplierOrLogout(credentials.user);
    } else {
      throw new Error(CURRENT_LANG() === ES ? "Este link no es válido" : "This link is not valid");
    }
  },
  refreshUserData: async () => {
    const user = await getCurrentUser();
    if (user) await setSupplierOrLogout(user);
  },
  loginOtp: async (token: string) => {
    const userCredential = await signInWithCustomToken(auth, token);
    setSupplierOrLogout(userCredential.user);
  },
  setSupplierOrLogout: async (user: User) => {
    await setSupplierOrLogout(user);
  },
  changePassword: async (password: string) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw CURRENT_LANG() === ES ? "Tiempo de sesión finalizado" : "No user session";
      }
      await user?.updatePassword(password);
    } catch (error) {
      throw { error };
    }
  },
  async signup(values) {
    await refreshSeeriApiConfig();
    await createSupplierWithoutId(values);
    await get().login(values);
  },
}));

const setSupplierOrLogout = async (user: User) => {
  await refreshSeeriApiConfig();
  const userToken = await user.getIdTokenResult();
  const userId = userToken?.claims["seeri_user_id"] || userToken?.claims["user_id"];
  const supplierId = userToken?.claims["supplier_id"];
  const isSupplier = userId === supplierId;

  try {
    const commercial = isSupplier ? null : await getUserProfile();
    const supplier = await getTenantProfile();

    useAuth.getState().setCommercial(commercial != null ? supplier : null, commercial);
    useAuth.getState().setUser(supplier);
    localStorage.setItem("country", supplier.countryCode ?? "co");

    // Track user login in Amplitude
    identifyUser(commercial, supplier);
    trackLogin();

    // Register FCM token for push notifications
    const fcmUserId = commercial?.id ?? supplier.id;
    if (fcmUserId) {
      requestFcmToken()
        .then((token) => {
          if (token) {
            upsertFcmToken(fcmUserId, token, "WEB").catch(logError);
          }
        })
        .catch(logError);
    }
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      await firebaseLogout();
      useAuth.getState().setUser({} as TSupplier);
      useAuth.getState().setCommercial(null, null);
    }
    showToastError({
      message:
        CURRENT_LANG() === ES
          ? "Error con el usuario, por favor contacta a soporte."
          : "User error, please contact support.",
    });
  } finally {
    await refreshSeeriApiConfig(localStorage.getItem("country") ?? "");
  }
};
